// file: C:/dartProBackend/dart-admin/src/profile/ProfilePage.tsx
import * as React from "react";
import {
  SimpleForm,
  TextInput,
  DateTimeInput,
  useNotify,
  useRefresh,
  Toolbar,
  SaveButton,
} from "react-admin";
import {
  Box,
  Divider,
  Typography,
  Card,
  CardContent,
  Stack,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { httpClient } from "../httpClient";
import { useClub } from "../club/ClubContext";
import { AvatarUploadInput } from "./AvatarUploadInput";

const apiUrl = "/api";

type NotificationSettings = {
  email_on_event_created: boolean;
  email_on_event_changed: boolean;
  email_on_event_cancelled: boolean;
  push_on_event_created: boolean;
  push_on_event_changed: boolean;
  push_on_event_cancelled: boolean;
  email_on_attendance_response_due: boolean;
  push_on_attendance_response_due: boolean;
  email_on_attendance_maybe_followup: boolean;
  push_on_attendance_maybe_followup: boolean;
};

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  email_on_event_created: false,
  email_on_event_changed: false,
  email_on_event_cancelled: false,
  push_on_event_created: false,
  push_on_event_changed: false,
  push_on_event_cancelled: false,
  email_on_attendance_response_due: false,
  push_on_attendance_response_due: false,
  email_on_attendance_maybe_followup: false,
  push_on_attendance_maybe_followup: false,
};

function formatLeadTime(hours: number | null): string {
  if (!hours || hours <= 0) return "";
  if (hours % 24 === 0 && hours >= 24) {
    const days = hours / 24;
    return `${days} Tag${days !== 1 ? "en" : ""} vorher`;
  }
  return `${hours} Stunde${hours !== 1 ? "n" : ""} vorher`;
}

function TabPanel(props: {
  value: number;
  index: number;
  children: React.ReactNode;
}) {
  const { value, index, children } = props;
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export function ProfilePage() {
  const notify = useNotify();
  const refresh = useRefresh();
  const [initial, setInitial] = React.useState<any | null>(null);
  const { clubId } = useClub();

  const [tab, setTab] = React.useState(0);

  // Notification settings state
  const [notifLoading, setNotifLoading] = React.useState(true);
  const [notifSaving, setNotifSaving] = React.useState(false);
  const [notifDirty, setNotifDirty] = React.useState(false);
  const [notif, setNotif] = React.useState<NotificationSettings>(
    DEFAULT_NOTIFICATION_SETTINGS
  );

  // Vom Verein konfigurierte Erinnerungs-Vorlaufzeiten (siehe /club/notifications)
  const [reminderHours, setReminderHours] = React.useState<{
    attendance_response_due_hours: number | null;
    attendance_maybe_followup_hours: number | null;
  }>({ attendance_response_due_hours: null, attendance_maybe_followup_hours: null });

  React.useEffect(() => {
    if (!clubId) return;
    (async () => {
      try {
        const { json } = await httpClient(
          `${apiUrl}/clubs/${clubId}/notification-recipients/settings`
        );
        setReminderHours({
          attendance_response_due_hours: json?.attendance_response_due_hours ?? null,
          attendance_maybe_followup_hours: json?.attendance_maybe_followup_hours ?? null,
        });
      } catch {
        // still leise: Zeiten sind nur ein Hinweistext, kein Blocker
      }
    })();
  }, [clubId]);

  // Load profile
  React.useEffect(() => {
    (async () => {
      try {
        const url = clubId ? `${apiUrl}/me?club_id=${clubId}` : `${apiUrl}/me`;
        const { json } = await httpClient(url);
        setInitial({
          ...json,
          avatar: json.avatar_url ?? null, // <- wichtig!
          points: json.points ?? 0,
        });
      } catch (e: any) {
        notify(e?.message ?? "Profil konnte nicht geladen werden", {
          type: "error",
        });
      }
    })();
  }, [notify, clubId]);

  // Load notification settings
  const loadNotif = React.useCallback(async () => {
    setNotifLoading(true);
    try {
      // NOTE: dein Backend /me/notification-settings hat keine club_id -> wir schicken trotzdem,
      // falls du es später pro-club machen willst (Backend kann es ignorieren)
      const url = clubId
        ? `${apiUrl}/me/notification-settings?club_id=${clubId}`
        : `${apiUrl}/me/notification-settings`;

      const { json } = await httpClient(url);
      setNotif({ ...DEFAULT_NOTIFICATION_SETTINGS, ...(json ?? {}) });
      setNotifDirty(false);
    } catch (e: any) {
      // fallback auf defaults, aber nicht hart failen
      setNotif(DEFAULT_NOTIFICATION_SETTINGS);
      setNotifDirty(false);
      notify(e?.message ?? "Benachrichtigungen konnten nicht geladen werden", {
        type: "warning",
      });
    } finally {
      setNotifLoading(false);
    }
  }, [clubId, notify]);

  React.useEffect(() => {
    loadNotif();
  }, [loadNotif]);

  const ProfileToolbar = () => (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  );

  const onSubmit = async (values: any) => {
    try {
      const { avatar, avatar_url, avatar_updated_at, ...profile } = values;

      // 1) Profil speichern: PUT /me?club_id=...
      const putUrl = clubId ? `${apiUrl}/me?club_id=${clubId}` : `${apiUrl}/me`;
      await httpClient(putUrl, { method: "PUT", body: JSON.stringify(profile) });

      // 2) Avatar Upload: POST /me/avatar?club_id=...
      if (avatar instanceof File) {
        const form = new FormData();
        form.append("file", avatar);

        const upUrl = clubId
          ? `${apiUrl}/me/avatar?club_id=${clubId}`
          : `${apiUrl}/me/avatar`;
        await httpClient(upUrl, { method: "POST", body: form });
      }

      notify("Profil gespeichert", { type: "info" });
      refresh();
    } catch (e: any) {
      notify(e?.message ?? "Fehler beim Speichern", { type: "error" });
    }
  };

  const setNotifField =
    (key: keyof NotificationSettings) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNotif((prev) => ({ ...prev, [key]: e.target.checked }));
      setNotifDirty(true);
    };

  const saveNotif = async () => {
    setNotifSaving(true);
    try {
      const url = clubId
        ? `${apiUrl}/me/notification-settings?club_id=${clubId}`
        : `${apiUrl}/me/notification-settings`;

      await httpClient(url, { method: "PUT", body: JSON.stringify(notif) });

      setNotifDirty(false);
      notify("Benachrichtigungen gespeichert", { type: "info" });
    } catch (e: any) {
      notify(e?.message ?? "Fehler beim Speichern der Benachrichtigungen", {
        type: "error",
      });
    } finally {
      setNotifSaving(false);
    }
  };

  if (!initial) return <Typography sx={{ p: 2 }}>Lade Profil…</Typography>;

  return (
    <Box sx={{ p: 2, maxWidth: 820 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Mein Profil
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Card sx={{ borderRadius: 1 }}>
        <CardContent sx={{ pb: 1 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1 }}
          >
            <Tab label="Profil" />
            <Tab label="Benachrichtigungen" />
          </Tabs>

          <Divider />

          {/* TAB 1: Profil */}
          <TabPanel value={tab} index={0}>
            <SimpleForm
              defaultValues={initial}
              onSubmit={onSubmit}
              toolbar={<ProfileToolbar />}
            >
              <AvatarUploadInput source="avatar" points={initial.points} />
              <TextInput source="first_name" label="Vorname" fullWidth />
              <TextInput source="last_name" label="Nachname" fullWidth />
              <TextInput source="email" label="E-Mail" fullWidth disabled />

              <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 2 }}>
                Abwesenheit
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                Termine und Job-Zuweisungen in diesem Zeitraum werden automatisch abgesagt.
              </Alert>
              <DateTimeInput source="absence_start" label="Abwesend von" />
              <DateTimeInput source="absence_end" label="Abwesend bis" />
            </SimpleForm>
          </TabPanel>

          {/* TAB 2: Notifications */}
          <TabPanel value={tab} index={1}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Benachrichtigungen
              </Typography>

              <Alert severity="info">
                Lege fest, ob du bei Terminen eine Email oder Push-Benachrichtigung erhalten willst.
              </Alert>

              {notifLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Lade…
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Email
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.email_on_event_created}
                        onChange={setNotifField("email_on_event_created")}
                      />
                    }
                    label="Neuer Termin"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.email_on_event_changed}
                        onChange={setNotifField("email_on_event_changed")}
                      />
                    }
                    label="Termin geändert / verschoben"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.email_on_event_cancelled}
                        onChange={setNotifField("email_on_event_cancelled")}
                      />
                    }
                    label="Termin abgesagt"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.email_on_attendance_response_due}
                        onChange={setNotifField("email_on_attendance_response_due")}
                      />
                    }
                    label={`Erinnerung: Zusage fehlt noch${
                      reminderHours.attendance_response_due_hours
                        ? ` (${formatLeadTime(reminderHours.attendance_response_due_hours)})`
                        : ""
                    }`}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.email_on_attendance_maybe_followup}
                        onChange={setNotifField("email_on_attendance_maybe_followup")}
                      />
                    }
                    label={`Erinnerung: Status "Vielleicht"${
                      reminderHours.attendance_maybe_followup_hours
                        ? ` (${formatLeadTime(reminderHours.attendance_maybe_followup_hours)})`
                        : ""
                    }`}
                  />

                  <Divider />

                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Push (PWA/WebPush)
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.push_on_event_created}
                        onChange={setNotifField("push_on_event_created")}
                      />
                    }
                    label="Neuer Termin"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.push_on_event_changed}
                        onChange={setNotifField("push_on_event_changed")}
                      />
                    }
                    label="Termin geändert / verschoben"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.push_on_event_cancelled}
                        onChange={setNotifField("push_on_event_cancelled")}
                      />
                    }
                    label="Termin abgesagt"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.push_on_attendance_response_due}
                        onChange={setNotifField("push_on_attendance_response_due")}
                      />
                    }
                    label={`Erinnerung: Zusage fehlt noch${
                      reminderHours.attendance_response_due_hours
                        ? ` (${formatLeadTime(reminderHours.attendance_response_due_hours)})`
                        : ""
                    }`}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notif.push_on_attendance_maybe_followup}
                        onChange={setNotifField("push_on_attendance_maybe_followup")}
                      />
                    }
                    label={`Erinnerung: Status "Vielleicht"${
                      reminderHours.attendance_maybe_followup_hours
                        ? ` (${formatLeadTime(reminderHours.attendance_maybe_followup_hours)})`
                        : ""
                    }`}
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ pt: 1 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={loadNotif}
                      disabled={notifSaving}
                    >
                      Zurücksetzen
                    </Button>
                    <Button
                      variant="contained"
                      onClick={saveNotif}
                      disabled={notifSaving || !notifDirty}
                    >
                      {notifSaving ? "Speichere…" : "Speichern"}
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}
